<template>
  <v-dialog
    :value="value"
    max-width="640"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="primary white--text">Cookie preferences</v-card-title>
      <v-card-text class="pt-4">
        <p class="text-body-2">
          We use cookies and similar technologies to run this site and, with
          your consent, to analyse how it is used and deliver relevant content.
          You can change your choices at any time via the "Manage cookies" link
          at the bottom of the page.
        </p>

        <v-expansion-panels flat accordion multiple>
          <v-expansion-panel
            v-for="cat in categories"
            :key="cat.key"
          >
            <v-expansion-panel-header class="px-0">
              <div class="d-flex align-center" style="width: 100%">
                <div class="flex-grow-1">
                  <div class="cookie-category__title">{{ cat.label }}</div>
                  <div class="text-body-2 text--secondary">
                    {{ cat.description }}
                  </div>
                </div>
                <div class="ml-3" @click.stop>
                  <v-switch
                    v-if="cat.key === 'necessary'"
                    :input-value="true"
                    disabled
                    inset
                    hide-details
                    color="primary"
                    class="mt-0"
                  />
                  <v-switch
                    v-else
                    v-model="choices[cat.key]"
                    inset
                    hide-details
                    color="primary"
                    class="mt-0"
                    :aria-label="cat.label"
                  />
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="cat.items.length === 0" class="text-body-2 text--secondary">
                No cookies in this category currently. Reserved for future use.
              </div>
              <table v-else class="cookie-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in cat.items" :key="item.name">
                    <td><code>{{ item.name }}</code></td>
                    <td>{{ item.purpose }}</td>
                    <td>{{ item.duration }}</td>
                  </tr>
                </tbody>
              </table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions>
        <v-btn text @click="rejectAll">Reject all</v-btn>
        <v-btn text @click="acceptAll">Accept all</v-btn>
        <v-spacer />
        <v-btn color="primary" @click="save">Save preferences</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import cookieConsent from "@/common/cookieConsent";

const CATEGORY_DEFS = [
  {
    key: "necessary",
    label: "Strictly necessary (always on)",
    description:
      "Required for the site to function — authentication, session, tenant, and remembering your consent choice.",
    items: [
      {
        name: "rm_cookie_consent",
        purpose: "Records your cookie consent choices",
        duration: "12 months",
      },
      {
        name: "Authentication session",
        purpose: "Keeps you logged in to your account",
        duration: "Session",
      },
      {
        name: "RMTenant",
        purpose: "Identifies which tenant (client) you are accessing",
        duration: "Session",
      },
      {
        name: "Local browser storage",
        purpose:
          "Remembers recent documents, UI preferences, and impersonation state",
        duration: "Persistent",
      },
    ],
  },
  {
    key: "analytics",
    label: "Analytics",
    description:
      "Helps us understand how the site is used so we can improve it. Loaded only if enabled.",
    items: [
      {
        name: "_ga",
        purpose: "Google Analytics — distinguishes unique users",
        duration: "2 years",
      },
      {
        name: "_gid",
        purpose: "Google Analytics — distinguishes unique users",
        duration: "24 hours",
      },
      {
        name: "_ga_<container>",
        purpose: "Google Analytics 4 — session state",
        duration: "2 years",
      },
    ],
  },
];

export default {
  name: "CookiePreferences",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      choices: this.initChoices(),
      categories: CATEGORY_DEFS,
    };
  },
  watch: {
    value(open) {
      if (open) this.choices = this.initChoices();
    },
  },
  methods: {
    initChoices() {
      const current = cookieConsent.getConsent() || {};
      const out = {};
      cookieConsent.CATEGORIES.forEach((c) => {
        out[c] = !!current[c];
      });
      return out;
    },
    applyChoices(choices) {
      const prev = cookieConsent.getConsent();
      const next = cookieConsent.setConsent(choices);
      this.$emit("saved", next);
      this.$emit("input", false);
      const downgraded = cookieConsent.CATEGORIES.some(
        (c) => prev && prev[c] && !next[c]
      );
      if (downgraded) {
        setTimeout(() => location.reload(), 100);
      }
    },
    save() {
      this.applyChoices(this.choices);
    },
    acceptAll() {
      const all = {};
      cookieConsent.CATEGORIES.forEach((c) => { all[c] = true; });
      this.choices = all;
      this.applyChoices(all);
    },
    rejectAll() {
      const none = {};
      cookieConsent.CATEGORIES.forEach((c) => { none[c] = false; });
      this.choices = none;
      this.applyChoices(none);
    },
  },
};
</script>

<style scoped>
.cookie-category__title {
  font-weight: 600;
  font-size: 15px;
}
.cookie-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.cookie-table th,
.cookie-table td {
  text-align: left;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  vertical-align: top;
}
.cookie-table th {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
}
.cookie-table code {
  font-size: 12px;
  background: rgba(0, 0, 0, 0.05);
  padding: 1px 4px;
  border-radius: 3px;
}
</style>
